import { create } from 'zustand';
import { formatDistanceToNow } from 'date-fns';

export interface UserStatus {
  userId: string;
  isOnline: boolean;
  lastSeen: string;
  empName?: string;
  avatar?: string;
  jobName?: string;
}

interface UserStatusStore {
  statuses: Map<string, UserStatus>;
  lastSyncTime: Date | null;

  // Basic actions
  updateStatus: (userId: string, status: Omit<UserStatus, 'userId'>) => void;
  getStatus: (userId: string) => UserStatus | undefined;
  clearStatuses: () => void;
  setMultipleStatuses: (users: UserStatus[]) => void;
  setLastSyncTime: (time: Date) => void;

  // Computed selectors
  getOnlineCount: () => number;
  getOnlineUsers: () => UserStatus[];
  isUserOnline: (userId: string) => boolean;
  getLastSeenFormatted: (userId: string) => string | null;
  getAllStatuses: () => UserStatus[];
}

export const useUserStatusStore = create<UserStatusStore>((set, get) => ({
  statuses: new Map(),
  lastSyncTime: null,

  // Basic actions
  updateStatus: (userId, status) => {
    set((state) => {
      const newStatuses = new Map(state.statuses);
      newStatuses.set(userId, { userId, ...status });
      return { statuses: newStatuses };
    });
  },

  getStatus: (userId) => {
    return get().statuses.get(userId);
  },

  clearStatuses: () => {
    set({ statuses: new Map(), lastSyncTime: null });
  },

  setMultipleStatuses: (users) => {
    set((state) => {
      const newStatuses = new Map(state.statuses);
      users.forEach((user) => {
        newStatuses.set(user.userId, user);
      });
      return { statuses: newStatuses, lastSyncTime: new Date() };
    });
  },

  setLastSyncTime: (time) => {
    set({ lastSyncTime: time });
  },

  // Computed selectors
  getOnlineCount: () => {
    const statuses = Array.from(get().statuses.values());
    // Calculate isOnline based on lastSeen (within last 60 seconds)
    return statuses.filter((status) => {
      if (!status.lastSeen) return false;
      try {
        const lastSeenDate = new Date(status.lastSeen);
        const now = new Date();
        const diffInSeconds = (now.getTime() - lastSeenDate.getTime()) / 1000;
        return diffInSeconds < 60;
      } catch {
        return false;
      }
    }).length;
  },

  getOnlineUsers: () => {
    const statuses = Array.from(get().statuses.values());
    // Calculate isOnline based on lastSeen (within last 60 seconds)
    return statuses.filter((status) => {
      if (!status.lastSeen) return false;
      try {
        const lastSeenDate = new Date(status.lastSeen);
        const now = new Date();
        const diffInSeconds = (now.getTime() - lastSeenDate.getTime()) / 1000;
        return diffInSeconds < 60;
      } catch {
        return false;
      }
    });
  },

  isUserOnline: (userId) => {
    const status = get().statuses.get(userId);
    if (!status || !status.lastSeen) return false;
    // Calculate based on lastSeen (within last 60 seconds)
    try {
      const lastSeenDate = new Date(status.lastSeen);
      const now = new Date();
      const diffInSeconds = (now.getTime() - lastSeenDate.getTime()) / 1000;
      return diffInSeconds < 60;
    } catch {
      return false;
    }
  },

  getLastSeenFormatted: (userId) => {
    const status = get().statuses.get(userId);
    if (!status) return null;

    if (!status.lastSeen) {
      return 'Never';
    }

    try {
      // Always return "منذ X" format
      const distance = formatDistanceToNow(new Date(status.lastSeen));
      return `منذ ${distance}`;
    } catch (error) {
      return 'Unknown';
    }
  },

  getAllStatuses: () => {
    return Array.from(get().statuses.values());
  },
}));
